import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn, formatRelativeTime } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { notificationsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  LayoutDashboard,
  Users,
  FileText,
  ClipboardList,
  Settings,
  Shield,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Receipt,
  Sparkles,
  Calendar,
  UserPlus,
  FileDown,
  FilePieChart,
  Database,
  Bell,
  Check,
  Trash2,
  ExternalLink,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_emr-payment-track/artifacts/s9yj95ue_Photoroom_20260128_234628.PNG";

const navItems = [
  { 
    title: 'Dashboard', 
    icon: LayoutDashboard, 
    href: '/app/dashboard',
    permission: null 
  },
  { 
    title: 'Scheduling', 
    icon: Calendar, 
    href: '/app/scheduling',
    permission: 'appointments:read' 
  },
  { 
    title: 'Patients', 
    icon: Users, 
    href: '/app/patients',
    permission: 'patients:read' 
  },
  { 
    title: 'Patient Summaries', 
    icon: FilePieChart, 
    href: '/app/patient-summaries',
    permission: null,
    roles: ['admin', 'physician']
  },
  { 
    title: 'Forms Library', 
    icon: ClipboardList, 
    href: '/app/forms',
    permission: 'forms:read' 
  },
  { 
    title: 'Data Export', 
    icon: Database, 
    href: '/app/data-export',
    permission: null,
    adminOnly: true
  },
  { 
    title: 'Invoices', 
    icon: Receipt, 
    href: '/app/invoices',
    permission: 'invoices:read' 
  },
  { 
    title: 'Patient Requests', 
    icon: UserPlus, 
    href: '/app/patient-requests',
    permission: null,
    roles: ['admin', 'physician']
  },
  { 
    title: 'Event Quotes', 
    icon: Sparkles, 
    href: '/app/event-quotes',
    permission: null,
    adminOnly: true
  },
  { 
    title: 'User Management', 
    icon: Shield, 
    href: '/app/users',
    permission: 'users:read' 
  },
  { 
    title: 'Settings', 
    icon: Settings, 
    href: '/app/settings',
    permission: null 
  },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const [notifRes, countRes] = await Promise.all([
          notificationsAPI.getAll({ limit: 10 }),
          notificationsAPI.getUnreadCount()
        ]);
        setNotifications(notifRes.data || []);
        setUnreadCount(countRes.data?.unread_count || 0);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    };

    if (user) {
      fetchNotifications();
      // Poll every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setNotificationOpen(false);
    }
  };

  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex flex-col h-screen premium-sidebar transition-all duration-300',
          collapsed ? 'w-[72px]' : 'w-[280px]'
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={cn(
          'flex items-center h-20 px-5 border-b border-slate-100',
          collapsed ? 'justify-center' : 'gap-4'
        )}>
          <div className="relative">
            <img 
              src={LOGO_URL} 
              alt="Apex Medical Group" 
              className="w-11 h-11 rounded-xl object-contain shadow-sm"
            />
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-primary rounded-full border-2 border-white" />
          </div>
          {!collapsed && (
            <div className="animate-fadeIn">
              <h1 className="font-bold text-lg tracking-tight text-slate-900">Apex Medical</h1>
              <p className="text-xs text-primary font-medium">EMR System</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-6">
          <nav className="px-3 space-y-1">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.href || 
                             location.pathname.startsWith(item.href + '/');
              
              const NavLink = (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-${item.title.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    'relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200',
                    'text-slate-500 hover:text-slate-900 hover:bg-slate-50',
                    isActive && 'bg-primary/5 text-primary hover:bg-primary/10',
                    collapsed && 'justify-center px-3'
                  )}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />
                  )}
                  <item.icon className={cn(
                    'w-5 h-5 flex-shrink-0 transition-colors',
                    isActive && 'text-primary'
                  )} strokeWidth={1.5} />
                  {!collapsed && (
                    <span className="font-medium text-sm">{item.title}</span>
                  )}
                </Link>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      {NavLink}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium bg-slate-900 text-white">
                      {item.title}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return NavLink;
            })}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="border-t border-slate-100 p-4">
          {!collapsed && user && (
            <div className="flex items-center gap-3 mb-4 animate-fadeIn">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/10">
                <span className="font-bold text-sm text-primary">
                  {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate text-slate-900">{user.full_name}</p>
                <p className="text-xs text-primary truncate font-medium">
                  {user.professional_title || user.role}
                </p>
              </div>
            </div>
          )}
          
          <div className={cn('flex gap-2', collapsed && 'flex-col')}>
            {/* Notifications Bell */}
            <Popover open={notificationOpen} onOpenChange={setNotificationOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 rounded-xl hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all relative"
                      data-testid="notifications-btn"
                    >
                      <Bell className="h-5 w-5" strokeWidth={1.5} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side={collapsed ? 'right' : 'top'} className="bg-slate-900 text-white">
                  Notifications {unreadCount > 0 && `(${unreadCount} unread)`}
                </TooltipContent>
              </Tooltip>
              <PopoverContent 
                side={collapsed ? 'right' : 'top'} 
                align="end"
                className="w-80 p-0 bg-white border-slate-200 shadow-xl"
              >
                <div className="flex items-center justify-between p-3 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-primary hover:text-primary/80"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Bell className="h-8 w-8 text-slate-300 mb-2" />
                      <p className="text-sm text-slate-500">No notifications</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {notifications.map(notif => (
                        <div
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          className={cn(
                            "p-3 cursor-pointer transition-colors hover:bg-slate-50",
                            !notif.is_read && "bg-primary/5"
                          )}
                        >
                          <div className="flex items-start gap-2">
                            <div className={cn(
                              "w-2 h-2 rounded-full mt-1.5 shrink-0",
                              notif.priority === 'high' || notif.priority === 'urgent' 
                                ? "bg-red-500" 
                                : notif.is_read ? "bg-transparent" : "bg-primary"
                            )} />
                            <div className="flex-1 min-w-0">
                              <p className={cn(
                                "text-sm truncate",
                                !notif.is_read ? "font-semibold text-slate-900" : "text-slate-700"
                              )}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                                {notif.message}
                              </p>
                              <p className="text-[10px] text-slate-400 mt-1">
                                {formatRelativeTime(notif.created_at)}
                              </p>
                            </div>
                            {notif.link && (
                              <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggle}
                  className="h-10 w-10 rounded-xl hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all"
                  data-testid="toggle-sidebar"
                >
                  {collapsed ? (
                    <PanelLeft className="h-5 w-5" strokeWidth={1.5} />
                  ) : (
                    <PanelLeftClose className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side={collapsed ? 'right' : 'top'} className="bg-slate-900 text-white">
                {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="h-10 w-10 rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50 transition-all"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-5 w-5" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={collapsed ? 'right' : 'top'} className="bg-slate-900 text-white">
                Logout
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
};
